import React, { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Flex, Image, Text } from 'rebass';
import arcgisLogo from '../../assets/arcgis-logo.svg';
import Modal, { ModalBody } from '../../components/Modal';
import { Input } from '../../components/Form';
import { CopyButton } from '../../components/CopyButton/CopyButton';
import { gql } from '@/modes/waterSupplier/gql';

type OgcAssembliesUrlModalProps = {
  onClose: () => void;
};

const GENERATE_OGC_ASSEMBLIES_URLS = gql(`
  mutation GenerateOgcAssembliesUrls {
    generateOgcAssembliesUrls {
      ogcUrl
      geojsonUrl
    }
  }
`);

/**
 * Lets a water supplier generate a signed OGC API Features server URL
 * (the landing page of the discovery chain, not a single dataset), ready
 * to paste into ArcGIS as an "OGC feature layer" ("Add Layer from URL").
 * Currently exposes one collection (backflow assemblies), but the URL
 * itself is server-scoped so later collections show up automatically.
 */
const OgcAssembliesUrlModal: React.FC<OgcAssembliesUrlModalProps> = ({
  onClose,
}) => {
  const [generateUrls, { data, loading, error }] = useMutation(
    GENERATE_OGC_ASSEMBLIES_URLS
  );

  useEffect(() => {
    generateUrls();
  }, [generateUrls]);

  const ogcUrl = data?.generateOgcAssembliesUrls?.ogcUrl ?? '';
  const geojsonUrl = data?.generateOgcAssembliesUrls?.geojsonUrl ?? '';

  return (
    <Modal title="ArcGIS: OGC Feature Server URL" onClose={onClose}>
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Image
            src={arcgisLogo}
            alt="ArcGIS"
            height="28px"
            pb={3}
            sx={{ alignSelf: 'flex-start' }}
          />
          <Text pb={2} variant="body1">
            In ArcGIS:
          </Text>
          <Box as="ol" pb={3} pl={4} sx={{ '& li': { pb: 2 } }}>
            <Text as="li" variant="body1">
              Go to <strong>Content &rarr; + New Item</strong>
            </Text>
            <Text as="li" variant="body1">
              Choose{' '}
              <strong>
                &ldquo;URL &rarr; Link to an ArcGIS Server web service, CSV, OGC
                web service, kml, GeoJSON or a document&rdquo;
              </strong>
            </Text>
            <Text as="li" variant="body1">
              Paste this URL
            </Text>
            <Text as="li" variant="body1">
              Set the layer type to{' '}
              <strong>&ldquo;OGC Feature Layer&rdquo;</strong>
            </Text>
          </Box>
          {loading && <Text variant="body1">Generating URL...</Text>}
          {error && (
            <Text variant="body1" color="danger">
              Failed to generate URL: {error.message}
            </Text>
          )}
          {ogcUrl && (
            <Flex alignItems="center">
              <Input
                readOnly
                value={ogcUrl}
                onFocus={(e) => e.target.select()}
                sx={{ flexGrow: 1 }}
              />
              <CopyButton text={ogcUrl} label="ArcGIS URL" />
            </Flex>
          )}

          <Text pt={4} pb={2} variant="body1">
            Or, for a plain cacheable GeoJSON file of all active assemblies (no
            OGC pagination/discovery - just add it as a GeoJSON layer):
          </Text>
          {geojsonUrl && (
            <Flex alignItems="center">
              <Input
                readOnly
                value={geojsonUrl}
                onFocus={(e) => e.target.select()}
                sx={{ flexGrow: 1 }}
              />
              <CopyButton text={geojsonUrl} label="GeoJSON URL" />
            </Flex>
          )}
        </Flex>
      </ModalBody>
    </Modal>
  );
};

export default OgcAssembliesUrlModal;
